/** Competition wall-clock input. Calendar-only helpers cannot disambiguate DST. */
export function postingTimeInZone(
  value: string,
  timeZone: string,
): { instant: string | null; error: string | null } {
  if (!value) return { instant: null, error: null };
  const recovery = "Choose another competition time or leave blank to use the server time.";
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
  if (!match)
    return { instant: null, error: `Enter a valid competition date and time. ${recovery}` };
  const [year, month, day, hour, minute, second] = match.slice(1).map((v) => Number(v ?? 0));
  const anchor = Date.UTC(year, month - 1, day, hour, minute, second),
    date = new Date(anchor);
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day ||
    hour > 23 ||
    minute > 59 ||
    second > 59
  )
    return { instant: null, error: `Enter a valid competition date and time. ${recovery}` };
  try {
    if (!timeZone) throw new RangeError("Missing competition time zone");
    const formatter = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    const wall = (instant: number) => {
      const parts = Object.fromEntries(
        formatter
          .formatToParts(new Date(instant))
          .filter((p) => p.type !== "literal")
          .map((p) => [p.type, Number(p.value)]),
      );
      return Date.UTC(
        parts.year,
        parts.month - 1,
        parts.day,
        parts.hour,
        parts.minute,
        parts.second,
      );
    };
    // Gather actual IANA offsets on both sides of a possible transition, then
    // round-trip every candidate. A gap has no candidate and a fold has two.
    const offsets = new Set<number>();
    for (let hours = -48; hours <= 48; hours += 6) {
      const instant = anchor + hours * 3600000;
      offsets.add(wall(instant) - instant);
    }
    const candidates = [...offsets]
      .map((offset) => anchor - offset)
      .filter((instant) => wall(instant) === anchor);
    if (candidates.length === 1)
      return { instant: new Date(candidates[0]).toISOString(), error: null };
    return {
      instant: null,
      error: `This local time ${candidates.length ? "occurs twice when the clocks change" : "does not exist when the clocks change"} in ${timeZone}. ${recovery}`,
    };
  } catch {
    return {
      instant: null,
      error: `The competition time zone is invalid. Ask an administrator to correct it, or leave blank to use the server time.`,
    };
  }
}
